import DashboardLayout from "@/components/DashboardLayout";
import { ExportButton } from "@/components/ExportButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Briefcase, TrendingUp, DollarSign, Users, ArrowRight } from "lucide-react";
import { Link } from "wouter";

export default function Placements() {
  const { data: placements, isLoading } = trpc.placements.performance.useQuery();
  const { data: exportData } = trpc.exports.placementReport.useQuery();

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "$0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
  };

  const formatPercent = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0%";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-US").format(num);
  };

  const exportColumns = [
    { key: "placement_name", header: "Placement Name" },
    { key: "portfolio_type", header: "Portfolio Type" },
    { key: "placement_date", header: "Placement Date" },
    { key: "face_value", header: "Face Value", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "purchase_price", header: "Purchase Price", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "purchase_percent", header: "Purchase %", format: (v: any) => `${(parseFloat(v) * 100).toFixed(4)}%` },
    { key: "total_accounts", header: "Total Accounts" },
    { key: "collection_fee_percent", header: "Collection Fee %", format: (v: any) => `${parseFloat(v).toFixed(2)}%` },
    { key: "status", header: "Status" },
    { key: "gross_collected", header: "Gross Collected", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "net_collected", header: "Net Collected", format: (v: any) => `$${parseFloat(v).toFixed(2)}` },
    { key: "liquidation_rate", header: "Liquidation Rate", format: (v: any) => `${parseFloat(v).toFixed(2)}%` },
    { key: "active_count", header: "Active Accounts" },
    { key: "settled_count", header: "Settled Accounts" },
    { key: "payment_plan_count", header: "Payment Plan Accounts" },
    { key: "recalled_count", header: "Recalled Accounts" },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const placementData = Array.isArray(placements) ? placements : [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Placement Performance</h1>
            <p className="text-muted-foreground">Detailed breakdown by placement batch</p>
          </div>
          <ExportButton
            data={Array.isArray(exportData) ? exportData : []}
            filename="placement_report"
            columns={exportColumns}
          />
        </div>

        <div className="grid gap-6">
          {placementData.map((p: any) => (
            <Card key={p.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle>{p.placement_name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{p.portfolio_type} • Placed {p.placement_date}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                    p.status === 'active' ? 'bg-emerald-500/10 text-emerald-500' :
                    p.status === 'closed' ? 'bg-gray-500/10 text-gray-500' :
                    'bg-amber-500/10 text-amber-500'
                  }`}>
                    {p.status}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      Face Value
                    </div>
                    <p className="text-2xl font-bold">{formatCurrency(p.face_value)}</p>
                    <p className="text-xs text-muted-foreground">Purchase: {formatCurrency(p.purchase_price)} ({formatPercent(Number(p.purchase_percent) * 100)})</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      Accounts
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(p.total_accounts)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatNumber(p.active_count)} active, {formatNumber(p.settled_count)} settled
                    </p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <TrendingUp className="h-4 w-4" />
                      Collections
                    </div>
                    <p className="text-2xl font-bold text-emerald-500">{formatCurrency(p.gross_collected)}</p>
                    <p className="text-xs text-muted-foreground">Net: {formatCurrency(p.net_collected)}</p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      Liquidation & ROI
                    </div>
                    <p className="text-2xl font-bold">{formatPercent(p.liquidation_rate)}</p>
                    <p className={`text-sm font-medium ${Number(p.roi) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      ROI: {formatPercent(p.roi)}
                    </p>
                  </div>
                </div>

                {/* Account Status Breakdown */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-sm font-medium mb-3">Account Status Distribution</p>
                  <div className="flex gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="text-sm">Active: {formatNumber(p.active_count)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <span className="text-sm">Settled: {formatNumber(p.settled_count)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm">Payment Plan: {formatNumber(p.payment_plan_count)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                      <span className="text-sm">Recalled: {formatNumber(p.recalled_count)}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t flex justify-end">
                  <Link href={`/placement/${p.id}`}>
                    <button className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors font-medium">
                      View Details
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
